import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileArchive, Download, Calendar, Clock, User, Mail, Music, FileAudio, Mic, File } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const UploadsManagement = () => {
  const [uploads, setUploads] = useState([]);
  const [sessionUploads, setSessionUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mix-master'); // 'mix-master' or 'session-files'

  useEffect(() => {
    fetchUploads();
    fetchSessionUploads();
  }, []);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/uploads`);
      setUploads(response.data.uploads);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionUploads = async () => {
    try {
      const response = await axios.get(`${API}/admin/session-uploads`);
      setSessionUploads(response.data);
    } catch (error) {
      console.error('Error fetching session uploads:', error);
      toast.error('Failed to load session uploads');
    }
  };

  const handleDownload = async (uploadId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/uploads/download/${uploadId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };


  const handleSessionDownload = async (uploadId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/session-uploads/${uploadId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('File downloaded successfully!');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getFileTypeIcon = (type) => {
    switch (type) {
      case 'beat': return <Music className="text-purple-400" size={20} />;
      case 'reference': return <FileAudio className="text-blue-400" size={20} />;
      case 'vocal': return <Mic className="text-pink-400" size={20} />;
      default: return <File className="text-gray-400" size={20} />;
    }
  };


  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
        <div className="text-center text-gray-400">Loading uploads...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Customer File Uploads</h2>
        <p className="text-gray-400">All files uploaded by customers for their sessions</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-purple-500/20">
        <button
          onClick={() => setActiveTab('session-files')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'session-files'
              ? 'border-b-2 border-purple-500 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Session Files ({sessionUploads.length})
        </button>
        <button
          onClick={() => setActiveTab('mix-master')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'mix-master'
              ? 'border-b-2 border-purple-500 text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Mix & Master ({uploads.length})
        </button>
      </div>

      {/* Session Files Tab */}
      {activeTab === 'session-files' && (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          {sessionUploads.length === 0 ? (
            <div className="text-center py-12">
              <Music className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No session files uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sessionUploads.map((upload) => (
                <div
                  key={upload.id}
                  className="bg-black/40 border border-purple-500/20 rounded-lg p-4 hover:border-purple-500/40 transition-all"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* File Info */}
                    <div className="flex-1">
                      <div className="flex items-start space-x-3">
                        {getFileTypeIcon(upload.file_type)}
                        <div className="flex-1">
                          <h3 className="font-semibold text-white mb-2">{upload.filename}</h3>
                          
                          {/* Customer Details */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                            <div className="flex items-center space-x-2 text-sm">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-300">{upload.customer_name}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-300">{upload.customer_email}</span>
                            </div>
                          </div>
                          
                          {/* Upload Info */}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                            <span className="bg-purple-900/30 px-2 py-1 rounded capitalize">
                              {upload.file_type}
                            </span>
                            <span>{(upload.file_size / (1024 * 1024)).toFixed(2)} MB</span>
                            <span>{new Date(upload.created_at).toLocaleDateString()} at {new Date(upload.created_at).toLocaleTimeString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <div>
                      <button
                        onClick={() => handleSessionDownload(upload.id, upload.filename)}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all flex items-center space-x-2"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mix & Master Tab */}
      {activeTab === 'mix-master' && (
        <div className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-6">
          <div className="mb-4">
            <p className="text-gray-400 text-sm">Mix & Master project files (ZIP format)</p>
          </div>

      {uploads.length === 0 ? (
        <div className="text-center py-12">
          <FileArchive className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No uploads yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {uploads.map((upload) => (
            <div
              key={upload.id}
              className="bg-black/40 border border-purple-500/20 rounded-lg p-4 hover:border-purple-500/40 transition-all"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* File Info */}
                <div className="flex-1">
                  <div className="flex items-start space-x-3">
                    <FileArchive className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-2">{upload.filename}</h3>
                      
                      {/* Booking Details */}
                      {upload.booking_details && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                          <div className="flex items-center space-x-2 text-sm">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">{upload.booking_details.name}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">{upload.customer_email}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">{upload.booking_details.date}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-sm">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-300">{upload.booking_details.time}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Upload Info */}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                        <span className="bg-purple-900/30 px-2 py-1 rounded">
                          Upload #{upload.upload_number}
                        </span>
                        <span>{formatFileSize(upload.file_size)}</span>
                        <span>Uploaded {formatDate(upload.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download Button */}
                <div className="flex-shrink-0">
                  <button
                    onClick={() => handleDownload(upload.id, upload.filename)}
                    className="w-full lg:w-auto bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 font-semibold"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {uploads.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-400">{uploads.length}</div>
            <div className="text-xs text-gray-400 mt-1">Total Uploads</div>
          </div>
          <div className="bg-blue-900/20 border border-blue-500/20 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-400">
              {formatFileSize(uploads.reduce((sum, u) => sum + u.file_size, 0))}
            </div>
            <div className="text-xs text-gray-400 mt-1">Total Size</div>
          </div>
          <div className="bg-green-900/20 border border-green-500/20 rounded-lg p-4 text-center sm:col-span-1 col-span-2">
            <div className="text-2xl font-bold text-green-400">
              {new Set(uploads.map(u => u.booking_id)).size}
            </div>
            <div className="text-xs text-gray-400 mt-1">Unique Bookings</div>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default UploadsManagement;
